/**
 * @fileoverview Route guard for the two-factor verification page.
 * @description Redirects an already-authenticated visitor to their resolved destination (see
 * `resolveSignedInDestination` — 2FA doesn't apply once signed in), and redirects anyone with no
 * pending challenge at all back to /login — landing on /two-factor directly, without having just
 * passed credentials, shows no meaningful screen. A challenge whose code has merely expired is
 * deliberately still let through: that's a recoverable state the page itself handles (disabling
 * submit, enabling Resend), not an access-control concern. This guard's own `isAuthenticated`
 * redirect fires the instant useVerifyTwoFactor's setUser flips that flag true — often before that
 * hook's own explicit post-verify navigate has committed — so it has to resolve the exact same
 * destination that hook does (mustChangePassword included), not just resolveHomeRoute, or it can
 * win that race and bounce a forced-password-change user straight past /change-password.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { Navigate, Outlet } from 'react-router'

import { ROUTES } from '@/shared/constants/routes.constants'
import { resolveSignedInDestination } from '@/shared/lib/auth/resolve-home-route'
import { useAuth } from '@/shared/lib/auth/useAuth'
import { useTwoFactorChallenge } from '@/shared/lib/auth/useTwoFactorChallenge'

/**
 * @component
 */
function TwoFactorRoute() {
  const { isAuthenticated, user } = useAuth()
  const { challenge } = useTwoFactorChallenge()

  if (isAuthenticated) {
    return <Navigate to={resolveSignedInDestination(user)} replace />
  }

  if (!challenge) {
    return <Navigate to={ROUTES.LOGIN} replace />
  }

  return <Outlet />
}

export default TwoFactorRoute
